import * as React from 'react';
import { observer } from 'mobx-react';
import { Select, Option, Toggle } from '@payscale/design';
import { SidePanelListType, WorkflowStatusListType } from 'typings/enums';
import { workflowStatusFromListTypeMap } from 'nomst/Stores/Decision/Constants/workflowStatusMap';
import { managerListSidePanelStore } from 'utils/MobxDataManager/singletons';
import { NavigationStore } from 'nomst/Stores/Navigation/navigation.store';
import { Configuration } from 'nomst/Stores/Configuration/configuration';

interface IManagerListSidePanelHeaderProps {
    navigationStore: NavigationStore;
    sidePanelListType: SidePanelListType;
    workflowStatusType?: Nullable<WorkflowStatusListType>;
    budgetCycleId?: Nullable<number>;
    configuration: Configuration;
    budgetNamesMap?: Nullable<Map<number, string>>;
}

export class ManagerListSidePanelHeader extends
    React.Component<IManagerListSidePanelHeaderProps> {
    constructor(props: IManagerListSidePanelHeaderProps) {
        super(props);

        this.handleWorkflowStatusChangeLookupOptionSelection
            = this.handleWorkflowStatusChangeLookupOptionSelection.bind(this);
        this.handleOverBudgetChangeLookupOptionSelection
            = this.handleOverBudgetChangeLookupOptionSelection.bind(this);
        this.handleSelectAllChange = this.handleSelectAllChange.bind(this);
        this.handleAssignedToMeChange = this.handleAssignedToMeChange.bind(this);
    }

    render(): React.ReactNode {
        return (
            <div className="nomst-manager-list-header">
                {this.renderHeaderTitle()}
                {this.renderLookup()}
                {this.renderToggles()}
            </div>
        );
    }

    private async handleOverBudgetChangeLookupOptionSelection(
        _: undefined,
        budgetCycleId: number): Promise<void> {
        const { navigationStore, configuration } = this.props;
        const managerSourceIds = await managerListSidePanelStore.fetchOverBudgetManagerIds(budgetCycleId);

        navigationStore.navigateToOverBudgetManagersRoute(
            configuration.cycleId,
            managerSourceIds[0] ? managerSourceIds : [],
            budgetCycleId);
    }

    private async handleWorkflowStatusChangeLookupOptionSelection(
        _: undefined,
        workflowStatus: WorkflowStatusListType): Promise<void> {
        const { navigationStore, configuration } = this.props;
        const managerSourceIds = await managerListSidePanelStore.fetchWorkflowManagerIds(workflowStatus);

        navigationStore.navigateToWorkflowStatusManagersRoute(
            configuration.cycleId,
            managerSourceIds[0] ? managerSourceIds : [],
            workflowStatus);
    }

    private renderHeaderTitle(): React.ReactNode {
        const { sidePanelListType } = this.props;
        if (sidePanelListType === SidePanelListType.WorkflowStatus) {
            return (
                <h4 className="nomst-manager-list-header__subtitle">Team Progress</h4>
            );
        } else if (sidePanelListType === SidePanelListType.OverBudget) {
            return (
                <h4 className="nomst-manager-list-header__subtitle">
                    Teams:
                    <span className="nomst-manager-list-header__status">Over Budget</span>
                </h4>
            );
        }

        return null;
    }

    private renderLookup(): React.ReactNode {
        const { sidePanelListType, budgetCycleId, workflowStatusType } = this.props;

        if ((managerListSidePanelStore.managerWorkflowSummaryLookups &&
            managerListSidePanelStore.managerWorkflowSummaryLookups.length > 0) ||
            (managerListSidePanelStore.managerOverBudgetSummaryLookups &&
            managerListSidePanelStore.managerOverBudgetSummaryLookups.length)) {
            const value = sidePanelListType === SidePanelListType.OverBudget
                ? budgetCycleId
                : workflowStatusType;
            const onChangeFunction = sidePanelListType === SidePanelListType.OverBudget
                ? this.handleOverBudgetChangeLookupOptionSelection
                : this.handleWorkflowStatusChangeLookupOptionSelection;
            return (
                <Select
                    id="managerListLookupSelect"
                    className="nomst-manager-list-header__dropdown"
                    value={value}
                    onChange={onChangeFunction}
                    options={this.lookupOptions}
                />
            );
        }
        return null;
    }

    private renderToggles(): React.ReactNode {
        if (managerListSidePanelStore.isFirstLevelManager) {
            return null;
        }
        return (
            <div className="nomst-manager-list-header__toggles">
                <Option
                    checked={managerListSidePanelStore.selectAllIsChecked}
                    className="pxl-option--secondary nomst-manager-list-header__checkbox"
                    id="selectAllManagersCheckbox"
                    onChange={this.handleSelectAllChange}
                    text="All"
                />
                <div className="nomst-manager-list-header__assigned-me-toggle">
                    Assigned to me
                    <Toggle
                        id="assignedToMeToggle"
                        value={managerListSidePanelStore.assignedToMeOnly}
                        onChange={this.handleAssignedToMeChange}
                    />
                </div>
            </div>
        );
    }

    private get lookupOptions(): Array<ISelectOption<string>> {
        const { sidePanelListType, budgetNamesMap } = this.props;
        if (!managerListSidePanelStore.managerWorkflowSummaryLookups &&
            !managerListSidePanelStore.managerOverBudgetSummaryLookups) {
            return [];
        }

        const summaries = sidePanelListType === SidePanelListType.OverBudget ?
            managerListSidePanelStore.managerOverBudgetSummaryLookups :
            managerListSidePanelStore.managerWorkflowSummaryLookups;
        const options: Array<ISelectOption<string>> = [];
        summaries?.forEach((listTypeLookup) => {
            if (listTypeLookup.count > 0) {
                switch (sidePanelListType) {
                    case SidePanelListType.WorkflowStatus:
                        return options.push({
                            value: listTypeLookup.listType,
                            // tslint:disable-next-line:max-line-length
                            text: `${workflowStatusFromListTypeMap.get(listTypeLookup.listType)} (${listTypeLookup.count})`
                        });
                    case SidePanelListType.OverBudget:
                        return options.push({
                            value: listTypeLookup.listType,
                            // tslint:disable-next-line:max-line-length
                            text: `${budgetNamesMap?.get(parseInt(listTypeLookup.listType, 10)) ?? 0} (${listTypeLookup.count})`
                        });
                    default:
                        return options;
                }
            } else {
                return options;
            }});

        return options;
    }

    private handleSelectAllChange(_: undefined, value: IOnChangeEvent): void {
        const {
            navigationStore,
            configuration,
            sidePanelListType,
            workflowStatusType,
            budgetCycleId
        } = this.props;
        managerListSidePanelStore.toggleSelectAllManagers(value.target.checked);
        if (sidePanelListType === SidePanelListType.WorkflowStatus && workflowStatusType) {
            navigationStore.navigateToWorkflowStatusManagersRoute(
                configuration.cycleId,
                managerListSidePanelStore.selectedManagerIds,
                workflowStatusType);
        } else if (budgetCycleId) {
            navigationStore.navigateToOverBudgetManagersRoute(
                configuration.cycleId,
                managerListSidePanelStore.selectedManagerIds,
                budgetCycleId);
        }
    }

    private handleAssignedToMeChange(id: string, value: boolean): void {
        const {
            navigationStore,
            configuration,
            sidePanelListType,
            workflowStatusType,
            budgetCycleId
        } = this.props;
        managerListSidePanelStore.toggleAssignedToMe(value);
        if (sidePanelListType === SidePanelListType.WorkflowStatus && workflowStatusType) {
            navigationStore.navigateToWorkflowStatusManagersRoute(
                configuration.cycleId,
                managerListSidePanelStore.selectedManagerIds,
                workflowStatusType);
        } else if (budgetCycleId) {
            navigationStore.navigateToOverBudgetManagersRoute(
                configuration.cycleId,
                managerListSidePanelStore.selectedManagerIds,
                budgetCycleId);
        }
    }
}

export default observer(ManagerListSidePanelHeader);